// Snippets (U13): a saved answer whose text holds variables in braces, filled from the people on
// the message when it is inserted into the composer. A variable MeeMail cannot fill (no name
// known, or a name it does not know) becomes a visible `[[fill: …]]` placeholder, and a message
// with a placeholder left in it cannot be sent until the owner fills it. Pure; no runtime deps.
//
//   {first_name} {last_name} {name} {email}   the first person the message goes to (To)
//   {sender_first_name} {sender_name}         who wrote the message being answered
//   {my_first_name} {my_name}                 the name you send as
//   anything else in braces, e.g. {company}   left as [[fill: company]] for you to fill in
import { displayName, firstNameOf } from "./people.ts";

export interface SnippetPerson {
  name?: string | null | undefined;
  email?: string | null | undefined;
}

export interface SnippetContext {
  /** The first person in To. */
  recipient?: SnippetPerson | null;
  /** Who wrote the message being answered (a reply). */
  sender?: SnippetPerson | null;
  /** The From identity. */
  me?: SnippetPerson | null;
}

/** The variables a snippet can use, with what each becomes (shown in the Answer library). */
export const SNIPPET_VARIABLES: readonly { name: string; fills: string }[] = [
  { name: "first_name", fills: "first name of the first person in To" },
  { name: "last_name", fills: "their last name" },
  { name: "name", fills: "their full name" },
  { name: "email", fills: "their address" },
  { name: "sender_first_name", fills: "first name of whoever wrote the message you answer" },
  { name: "sender_name", fills: "their full name" },
  { name: "my_first_name", fills: "your first name, as you send" },
  { name: "my_name", fills: "your full name, as you send" },
];

const VARIABLE = /\{([a-z][a-z0-9_]{0,39})\}/g;
const PLACEHOLDER = /\[\[\s*fill:\s*([^\]\n]*?)\s*\]\]/gi;

/** `[[fill: first name]]`: what an unfilled variable becomes. */
export function placeholder(label: string): string {
  return `[[fill: ${label}]]`;
}

const lastNameOf = (name: string | null | undefined) => {
  const words = displayName(name).split(" ");
  return words.length > 1 ? (words.at(-1) ?? "") : "";
};

function valueOf(variable: string, ctx: SnippetContext): string {
  const { recipient, sender, me } = ctx;
  switch (variable) {
    case "first_name":
      return firstNameOf(recipient?.name);
    case "last_name":
      return lastNameOf(recipient?.name);
    case "name":
      return displayName(recipient?.name);
    case "email":
      return recipient?.email?.trim() ?? "";
    case "sender_first_name":
      return firstNameOf(sender?.name);
    case "sender_name":
      return displayName(sender?.name);
    case "my_first_name":
      return firstNameOf(me?.name);
    case "my_name":
      return displayName(me?.name);
    default:
      return "";
  }
}

export interface FilledSnippet {
  text: string;
  /** Labels of the variables left as placeholders, in order of first use. */
  unfilled: string[];
}

/** Fills a snippet's `{variables}`; what can't be filled becomes `[[fill: …]]`. */
export function fillSnippet(text: string, ctx: SnippetContext): FilledSnippet {
  const unfilled: string[] = [];
  const out = text.replace(VARIABLE, (_whole, variable: string) => {
    const value = valueOf(variable, ctx);
    if (value) return value;
    const label = variable.replace(/_/g, " ");
    if (!unfilled.includes(label)) unfilled.push(label);
    return placeholder(label);
  });
  return { text: out, unfilled };
}

/** Whether a text uses any `{variable}` (a snippet, not a plain saved answer). */
export function hasVariables(text: string): boolean {
  return new RegExp(VARIABLE.source).test(text);
}

/** The labels of the `[[fill: …]]` placeholders still in a text, in order, once each. */
export function findPlaceholders(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) {
    const label = (m[1] ?? "").trim() || "something";
    if (!out.includes(label)) out.push(label);
  }
  return out;
}

/** "Fill in first name and company before sending." (null when nothing is left to fill). */
export function placeholderProblem(texts: readonly string[]): string | null {
  const labels = [...new Set(texts.flatMap(findPlaceholders))];
  if (!labels.length) return null;
  const list =
    labels.length > 1
      ? `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`
      : (labels[0] ?? "something");
  return `Fill in ${list} before sending.`;
}
